// @owners { team: patients-team }
import { COLORS, SPACING, TYPOGRAPHY } from '@alto/design-library-tokens';
import { useScreenSize } from '@alto/design-system';
// eslint-disable-next-line @alto/no-pocky-import
import { FlexColumn } from '@alto/pocky';
import React from 'react';
import styled from 'styled-components';
import { palette } from './styles';

type ButtonTextProps = {
  disabled: boolean;
  isSMScreenOrBigger: boolean;
};
const ButtonText = styled.span<ButtonTextProps>`
  font-family: '${TYPOGRAPHY.FONT.HEADER.REGULAR}', serif;
  color: ${({ disabled }) => (disabled ? palette.disabledButtonTextGray : COLORS.TEXT_COLORS.PRIMARY)};
  font-size: 18px;
  line-height: 30px;
  ${({ isSMScreenOrBigger }) => !isSMScreenOrBigger && `font-size: 15px; line-height: 26px;`}
`;

type ButtonProps = {
  color: string;
  hoverColor: string;
  loading?: boolean;
  disabled: boolean;
};
const Button = styled.button<ButtonProps>`
  border: none;
  background-color: ${({ color, disabled }) => (disabled ? palette.disabledButtonGray : color)};
  transition: background-color 0.2s ease-in-out;
  height: 60px;
  display: flex;
  justify-content: center;
  border-radius: 38px;
  text-align: center;
  box-sizing: border-box;
  padding: 0 30px;
  :hover {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    background-color: ${({ hoverColor, disabled }) => (disabled ? palette.disabledButtonHover : hoverColor)};
  }
`;

const SpacedFlexColumn = styled(FlexColumn).attrs({
  verticallyAlignContent: true,
  spacing: SPACING.STATIC.XL.rem,
})<{ isSMScreenOrBigger: boolean }>`
  ${({ isSMScreenOrBigger }) =>
    !isSMScreenOrBigger &&
    `
      > *:not(:last-child) {
        margin-right: ${SPACING.STATIC.MD.rem};
      }
    `}
`;

export const Chevron = () => (
  <svg
    width="6px"
    height="12px"
    viewBox="0 0 6 12"
    version="1.1"
  >
    <g
      stroke="none"
      strokeWidth="1"
      fill="none"
      fillRule="evenodd"
    >
      <g
        fill={palette.brandNavy}
        fillRule="nonzero"
      >
        <path d="M1.56580259,0.0634377 C1.677199,0.1403148 1.8102956,0.194345 1.92888519,0.2637592 C1.99799857,0.3043315 2.06864956,0.344513 2.115904,0.4068094 C2.16113988,0.4659301 3.08441035,1.1793853 3.0765313,1.2409304 C3.15255868,1.2897667 3.1916842,1.3175143 3.24533779,1.3680824 C3.35294853,1.4695221 3.46881272,1.5637877 3.547015,1.6932867 C3.6175624,1.8097312 3.6873773,1.9258288 3.78567968,2.0152604 C3.890381,2.1107051 3.98881961,2.2126149 4.094649,2.3070665 C4.2057258,2.4061869 4.3303982,2.4917118 4.433954,2.5983532 C4.5397745,2.707452 4.6224535,2.8396921 4.7245472,2.9525851 C4.7764637,3.0102869 4.8555471,3.0411591 4.9092329,3.097159 C4.9459099,3.1357645 4.9540363,3.2025158 4.9859596,3.2460098 C5.0093746,3.2779099 5.049938,3.2934035 5.0844173,3.3145765 C5.0943058,3.3206081 5.1158903,3.3148086 5.1224908,3.3237233 C5.2200645,3.4565781 5.3197714,3.587394 5.413733,3.7236954 L5.3003527,3.6500924 C5.4752629,3.7567358 5.7461434,3.9799239 5.8956724,4.1699947 C6.0662424,4.3870429 6.0341463,4.4841277 6.0011458,4.5873237 C5.9038734,4.8920973 5.6298683,5.0033413 5.4690189,5.2419072 C5.3245304,5.4558198 5.1029917,5.5957852 4.9062672,5.7604974 C4.8168435,5.8352649 4.7496838,5.9327013 4.6664263,6.0138375 C4.513904,6.16231 4.3572018,6.3066701 4.2017868,6.45225 C4.0820334,6.5644825 3.95626798,6.6707706 3.84287841,6.789367 C3.74174665,6.895127 3.65765269,7.0177899 3.55873864,7.1257677 C3.51654995,7.1718133 3.45256504,7.196265 3.4043307,7.2363998 C3.29869698,7.324099 3.19663089,7.4153658 3.09337399,7.5055092 C3.03262155,7.558355 2.96859075,7.6079224 2.91250031,7.6653628 C2.80892503,7.7714451 2.77933929,7.9946471 2.66605706,8.1079293 C2.55277483,8.2212115 1.78698046,8.9715783 1.67161825,9.1023681 C1.55641992,9.2331869 1.51688579,9.389585 1.2992647,9.4079973 C1.0647391,9.2169143 0.80191769,8.9953093 0.6520931,8.7656152 C0.56391108,8.6304828 0.53275317,8.5179713 0.51208624,8.4167603 C0.46316032,8.1769974 0.44844455,7.9695559 0.53627248,7.8421947 C0.61314965,7.7307983 0.66717985,7.5977017 0.736594,7.4791121 C0.77716633,7.4099988 0.81734783,7.3393478 0.87964418,7.2920933 C0.93876488,7.2468574 1.65222012,6.323587 1.71376523,6.331466 C1.76260148,6.2554386 1.79034915,6.2163131 1.84091722,6.1626595 C1.94235693,6.0550488 2.0366225,5.9391846 2.16612151,5.8609823 C2.28256599,5.7904349 2.39866366,5.72062 2.48809518,5.6223176 C2.58353994,5.5176163 2.68544977,5.4191777 2.77990134,5.3133483 C2.87902175,5.2022715 2.96454661,5.0775991 3.07118798,4.9740433 C3.18028678,4.8682228 3.31252697,4.7855438 3.42541997,4.6834501 C3.48312177,4.6315336 3.51399393,4.5524503 3.56999386,4.4987645 C3.59378841,4.4761585 3.62827528,4.4643987 3.66141864,4.4512279 C3.65681375,4.4446 3.65216677,4.4390062 3.64749992,4.4334324 C3.57273239,4.3440086 3.47529605,4.2768489 3.39415987,4.1935915 C3.24568731,4.0410692 3.10132718,3.884367 2.9557473,3.728952 C2.84351479,3.6091985 2.73722675,3.4834332 2.61863037,3.3700436 C2.51287032,3.2689118 2.39020739,3.1848179 2.28222963,3.0859038 C2.23618405,3.0437151 2.21173233,2.9797302 2.17159748,2.9314959 C2.08389833,2.8258622 1.99263155,2.7237961 1.90248811,2.6205392 C1.84964231,2.5597867 1.80007488,2.4957559 1.74263453,2.4396655 C1.63655226,2.3360902 1.41335025,2.3065045 1.30006802,2.1932222 C1.18678579,2.07994 0.43641902,1.3141456 0.30562922,1.1987834 C0.17481045,1.0835851 0.01841232,1.044051 0,0.8264299 C0.19108305,0.5919043 0.41268799,0.3290829 0.64238213,0.1792583 C0.77751449,0.0910763 0.89002601,0.0599183 0.99123706,0.0392514 C1.23099993,-0.0096745 1.43844146,-0.02439027 1.56580259,0.0634377 Z" />
      </g>
    </g>
  </svg>
);

type CTAButtonProps = {
  readonly buttonText: string;
  readonly color: string;
  readonly hoverColor: string;
  readonly onClick: () => void;
  readonly loading?: boolean;
  readonly withChevron?: boolean;
  readonly disabled?: boolean;
};

export const CTAButton = ({
  buttonText,
  color,
  hoverColor,
  onClick,
  loading,
  withChevron = true,
  disabled = false,
}: CTAButtonProps) => {
  const { isSMScreenOrBigger } = useScreenSize();
  return (
    <Button
      color={color}
      hoverColor={hoverColor}
      onClick={onClick}
      loading={loading}
      disabled={disabled}
    >
      <SpacedFlexColumn isSMScreenOrBigger={isSMScreenOrBigger}>
        <ButtonText
          isSMScreenOrBigger={isSMScreenOrBigger}
          disabled={disabled}
        >
          {buttonText}
        </ButtonText>
        {withChevron ? <Chevron /> : null}
      </SpacedFlexColumn>
    </Button>
  );
};
